import { useCallback, useEffect, useRef, useState } from "react";
import { ColumnWidthOverrides, ListColumn } from "./types";

interface UseColumnResizeArgs<T> {
	/** The currently visible columns, in order, with their effective weights. */
	columns: ListColumn<T>[];
	/** Persist the new weights of the columns changed by a drag (called on release). */
	onCommit: (widths: ColumnWidthOverrides) => void;
	/** Revert a column to its default weight (double-click on its handle). */
	onReset: (key: string) => void;
	/** Minimum on-screen column width in px; the drag clamps to it. */
	minPx?: number;
}

interface ResizeHandleProps {
	onMouseDown: (e: React.MouseEvent) => void;
	onDoubleClick: (e: React.MouseEvent) => void;
	active: boolean;
}

export interface ColumnResize {
	/** The effective weight for a column, reflecting an in-progress drag. */
	weightOf: (key: string) => number;
	/** Handle props for a column, or undefined for the last column (nothing to its right). */
	handleProps: (key: string) => ResizeHandleProps | undefined;
	/** True while a drag is in progress (used to suppress row hover etc.). */
	isResizing: boolean;
}

interface DragSession {
	key: string;
	nextKey: string;
	startX: number;
	/** Table width in px at drag start, to convert px deltas into weight deltas. */
	tableWidth: number;
	/** Σ of all visible weights (kept constant: the pair's sum is preserved). */
	totalWeight: number;
	startWeight: number;
	startNextWeight: number;
	minWeight: number;
}

/**
 * Column resizing for a virtualized list table. A drag adjusts the grabbed
 * column and its right-hand neighbour by equal and opposite amounts, so the pair
 * sum — and thus the total weight — stays constant and the table keeps filling
 * its width (no horizontal scrollbar, compatible with `table-layout: fixed`).
 *
 * Widths are relative weights (see {@link ListColumn.weight}); a px drag delta is
 * converted to a weight delta via the measured table width. The live drag is kept
 * in local state for a smooth preview and only persisted on release. The last
 * visible column has no handle (there is nothing to its right to trade with).
 */
export function useColumnResize<T>({
	columns,
	onCommit,
	onReset,
	minPx = 60,
}: UseColumnResizeArgs<T>): ColumnResize {
	const [draft, setDraft] = useState<ColumnWidthOverrides | null>(null);
	const [activeKey, setActiveKey] = useState<string | null>(null);

	// Refs so the document-level listeners can stay stable (bound once) yet always
	// read the latest columns / commit callback / drag session.
	const columnsRef = useRef(columns);
	columnsRef.current = columns;
	const onCommitRef = useRef(onCommit);
	onCommitRef.current = onCommit;
	const sessionRef = useRef<DragSession | null>(null);
	const draftRef = useRef<ColumnWidthOverrides | null>(null);

	const onMouseMove = useRef((e: MouseEvent) => {
		const s = sessionRef.current;
		if (!s) return;
		const deltaWeight = ((e.clientX - s.startX) * s.totalWeight) / s.tableWidth;
		// Clamp so neither the column nor its neighbour drops below the min width.
		const maxWeight = s.startWeight + s.startNextWeight - s.minWeight;
		const nextWeight = Math.max(s.minWeight, Math.min(s.startWeight + deltaWeight, maxWeight));
		const next = {
			[s.key]: nextWeight,
			[s.nextKey]: s.startWeight + s.startNextWeight - nextWeight,
		};
		draftRef.current = next;
		setDraft(next);
	});

	const cleanup = useRef(() => {
		document.removeEventListener("mousemove", onMouseMove.current);
		document.removeEventListener("mouseup", onMouseUp.current);
		document.body.style.cursor = "";
		document.body.style.userSelect = "";
		sessionRef.current = null;
		draftRef.current = null;
		setActiveKey(null);
		setDraft(null);
	});

	const onMouseUp = useRef(() => {
		if (draftRef.current) onCommitRef.current(draftRef.current);
		cleanup.current();
	});

	const startDrag = useCallback(
		(key: string, e: React.MouseEvent) => {
			const cols = columnsRef.current;
			const index = cols.findIndex((c) => c.key === key);
			const next = cols[index + 1];
			if (index < 0 || !next) return;

			const table = (e.currentTarget as HTMLElement).closest("table");
			const tableWidth = table?.getBoundingClientRect().width ?? 0;
			if (!tableWidth) return;

			const totalWeight = cols.reduce((sum, c) => sum + c.weight, 0);

			// The resize should not also trigger the header's sort click.
			e.preventDefault();
			e.stopPropagation();

			sessionRef.current = {
				key,
				nextKey: next.key,
				startX: e.clientX,
				tableWidth,
				totalWeight,
				startWeight: cols[index].weight,
				startNextWeight: next.weight,
				minWeight: (minPx * totalWeight) / tableWidth,
			};
			draftRef.current = null;
			setActiveKey(key);

			document.addEventListener("mousemove", onMouseMove.current);
			document.addEventListener("mouseup", onMouseUp.current);
			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
		},
		[minPx]
	);

	// Safety net: drop listeners / restore the cursor if unmounted mid-drag.
	useEffect(() => () => cleanup.current(), []);

	const weightOf = useCallback(
		(key: string): number => {
			if (draft && draft[key] != null) return draft[key];
			return columns.find((c) => c.key === key)?.weight ?? 0;
		},
		[draft, columns]
	);

	const handleProps = useCallback(
		(key: string): ResizeHandleProps | undefined => {
			const index = columns.findIndex((c) => c.key === key);
			// No handle on the last column — nothing to its right to trade width with.
			if (index < 0 || index === columns.length - 1) return undefined;
			return {
				onMouseDown: (e) => startDrag(key, e),
				onDoubleClick: (e) => {
					e.preventDefault();
					e.stopPropagation();
					onReset(key);
				},
				active: activeKey === key,
			};
		},
		[columns, startDrag, onReset, activeKey]
	);

	return { weightOf, handleProps, isResizing: activeKey !== null };
}
